import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "@entry/navigation";

import { DateFormat, Flow, Routes, ShareGoalInviteAction, SipStatus } from "@ctypes";
import {
  useActionShareGoalInvite,
  useAddBeneficiary,
  useAddGoal,
  useCreateSip,
  useEditGoal,
  useErrorBanner,
  useGoalFlow,
  useGoalsContext,
  useOnboarding,
  usePauseSip,
  useProjectedChart,
  useResumeSip,
  useSuccessBanner,
  useUpdateSip,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { calculateProjectedAmount, convertDollarToCents, getHarvestAddGoalVariables } from "@utils";

export const useGoalDepositScreenFlow = () => {
  const { showErrorBanner } = useErrorBanner();
  const { showSuccessBanner } = useSuccessBanner();

  const navigate = useNavigate();
  const { resetToNext } = useOnboarding();

  const [loading, setLoading] = useState(false);

  const [addBeneficiary] = useAddBeneficiary();
  const [addGoal] = useAddGoal();
  const [editGoal] = useEditGoal();
  const [updateSip] = useUpdateSip();
  const [pauseSip] = usePauseSip();
  const [resumeSip] = useResumeSip();
  const [createSip] = useCreateSip();
  const [actionShareGoalInvite] = useActionShareGoalInvite();

  const {
    goalProps: {
      firstName,
      lastName,
      dateOfBirth: dob,
      beneficiaryType,
      accountType,
      beneficiaryId,
      goalName,
      goalType,
      flow,
      goalId,
      sipId,
      pendingInvite,
      ssn,
      harvestStandardDeduction,
      harvestLongTermCapitalGains,
      harvestRecommendedFloorUsd,
    },
    setGoalProps,
  } = useGoalFlow();

  const { sip } = useGoalsContext();

  const {
    targetYear,
    projectedAmount,
    recurringDepositType,
    recurringAmount: recurringAmountValue,
    contributeAfter18,
    interest,
    currentAge,
    projectedAge,
    initialBalance,
    multiplier,
  } = useProjectedChart();

  const dateOfBirth = dayjs(dob).format(DateFormat.User);

  const shouldPauseRecurringDeposits = recurringAmountValue === 0 && flow === Flow.EditGoal;
  const shouldUnpauseRecurringDeposits = sip?.status === SipStatus.Paused && flow === Flow.EditGoal;

  const handleAddGoalFlow = async () => {
    if (!beneficiaryId || !accountType) {
      return showErrorBanner();
    }

    try {
      setLoading(true);

      await addGoal({
        variables: {
          accountType,
          beneficiaryId,
          goalName,
          goalType,
          recurringDepositInCents: convertDollarToCents(recurringAmountValue),
          recurringDepositSchedule: recurringDepositType,
          targetAmountInCents: convertDollarToCents(projectedAmount),
          targetYear,
          contributionsAfter18: contributeAfter18,
          projectedRateOfReturn: interest,
          ...getHarvestAddGoalVariables({
            accountType,
            harvestStandardDeduction,
            harvestLongTermCapitalGains,
            harvestRecommendedFloorUsd,
          }),
        },
      });

      navigate(Routes.Accounts, { replace: true });
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingFlow = async () => {
    if (!accountType || !beneficiaryType) {
      return showErrorBanner();
    }

    if (!beneficiaryId && !dayjs(dob).isValid()) {
      return showErrorBanner();
    }

    try {
      setLoading(true);

      // Reuse the beneficiary when one already exists, so re entering this
      // screen after a create cannot add a duplicate.
      const id =
        beneficiaryId ??
        (
          await addBeneficiary({
            variables: {
              firstName,
              lastName,
              dateOfBirth,
              type: beneficiaryType,
              taxId: ssn,
            },
          })
        ).data?.addBeneficiary?.id;

      if (!id) return showErrorBanner();

      setGoalProps({ beneficiaryId: id });

      await addGoal({
        variables: {
          accountType,
          beneficiaryId: id,
          goalName,
          goalType,
          recurringDepositInCents: convertDollarToCents(recurringAmountValue),
          recurringDepositSchedule: recurringDepositType,
          targetAmountInCents: convertDollarToCents(projectedAmount),
          targetYear,
          contributionsAfter18: contributeAfter18,
          projectedRateOfReturn: interest,
          ...getHarvestAddGoalVariables({
            accountType,
            harvestStandardDeduction,
            harvestLongTermCapitalGains,
            harvestRecommendedFloorUsd,
          }),
        },
      });

      if (flow === Flow.Accounts) {
        navigate(Routes.Accounts, { replace: true });
      } else {
        await resetToNext();
      }
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const handleEditGoalFlow = async () => {
    if (!goalId) return showErrorBanner();

    try {
      setLoading(true);

      if (shouldPauseRecurringDeposits && sipId) {
        await pauseSip({
          variables: {
            sipId,
          },
        });
      } else if (shouldUnpauseRecurringDeposits && sipId) {
        await resumeSip({
          variables: {
            sipId,
          },
        });

        await updateSip({
          variables: {
            sipId,
            amountInCents: convertDollarToCents(recurringAmountValue),
            frequency: recurringDepositType,
          },
        });
      } else {
        const newProjectedAmount = calculateProjectedAmount({
          amount: recurringAmountValue,
          contributeAfter18,
          currentAge,
          projectedAge,
          recurringDepositType,
          initialBalance,
          multiplier,
        });

        await editGoal({
          variables: {
            goalId,
            projectedRateOfReturn: interest,
            contributionsAfter18: contributeAfter18,
            targetYear,
            targetAmountInCents: convertDollarToCents(newProjectedAmount),
          },
        });

        if (sipId) {
          await updateSip({
            variables: {
              sipId,
              amountInCents: convertDollarToCents(recurringAmountValue),
              frequency: recurringDepositType,
            },
          });
        } else {
          await createSip({
            variables: {
              goalId,
              amountInCents: convertDollarToCents(recurringAmountValue),
              frequency: recurringDepositType,
            },
          });
        }
      }

      navigate(-1);

      if (shouldPauseRecurringDeposits && sipId) {
        showSuccessBanner(i18n.t(Translations.GoalDepositRoutePauseSuccessful));
      } else if (shouldUnpauseRecurringDeposits && sipId) {
        showSuccessBanner(i18n.t(Translations.GoalDepositRouteUnPauseSuccessful));
      } else {
        showSuccessBanner(i18n.t(Translations.GoalDepositRouteUpdateSuccessful));
      }
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSharedGoalFlow = async () => {
    try {
      if (!pendingInvite?.id) return showErrorBanner();

      setLoading(true);

      const { data } = await actionShareGoalInvite({
        variables: {
          shareGoalInviteId: pendingInvite.id,
          action: ShareGoalInviteAction.Accept,
          accountType,
          contributionsAfter18: contributeAfter18,
          projectedRateOfReturn: interest,
          recurringDepositInCents: convertDollarToCents(recurringAmountValue),
          recurringDepositSchedule: recurringDepositType,
          targetAmountInCents: convertDollarToCents(projectedAmount),
        },
      });

      const acceptedGoalId = data?.actionShareGoalInvite?.id;

      if (acceptedGoalId) {
        navigate(Routes.Dashboard, { replace: true });
      } else {
        showErrorBanner();
      }
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    switch (flow) {
      case Flow.AddGoal:
        return handleAddGoalFlow();
      case Flow.EditGoal:
        return handleEditGoalFlow();
      case Flow.AcceptSharedGoal:
        return handleAcceptSharedGoalFlow();
      case Flow.Onboarding:
      default:
        return handleOnboardingFlow();
    }
  };

  return {
    handleSubmit,
    loading,
    shouldPauseRecurringDeposits,
    shouldUnpauseRecurringDeposits,
  };
};
